import React, { useEffect, useState, useRef } from "react";
import axios from "../../API/axios";
import { getOwnInfo, invalidateOwnInfo, invalidateUserConfig } from "../../Services/apiService";
import { formatDate, safeParse } from "../../utility/date";
import Flatpickr from "react-flatpickr";
import ReactSelect from "react-select";
import Select from "react-select";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { Loader } from "react-feather";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";
import {customStyles, wljSelectPortalProps} from "../../selectStyles";
import { useTranslation } from "react-i18next";
import useEscapeKeyDown from "../../hooks/useEscapeKeyDown";
import { Portal } from "../../components/Portal";


const ProfileBasicInfoModal = ({ onClose, getUserProfile, initialProfileInfo }) => {
  const { t } = useTranslation();
  useEscapeKeyDown(true, onClose);
  const sessionId = localStorage.getItem("sessionId");
  const language = localStorage.getItem("Lang");
  const langKey = language === "en" ? "" : language;
  const modalContentRef = useRef(null);

  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [genderValue, setGenderValue] = useState(null);
  const [genderOptionList, setGenderOptionList] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [cityData, setCityData] = useState([]);
  const [cityId, setCityId] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [citySearch, setCitySearch] = useState("");
  const selectedCountryRef = useRef(null);
  const selectedCityRef = useRef(null);
  const [websiteContent, setWebsiteContent] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [aboutContent, setAboutContent] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);

  const sameId = (a, b) =>
    a !== null &&
    a !== undefined &&
    a !== "" &&
    b !== null &&
    b !== undefined &&
    b !== "" &&
    String(a) === String(b);

  const pickValue = (source, keys) => {
    if (!source) return undefined;
    for (const key of keys) {
      const value = source[key];
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return undefined;
  };

  const pickFromSources = (sources, keys) => {
    for (const source of sources) {
      const value = pickValue(source, keys);
      if (value !== undefined) return value;
    }
    return undefined;
  };

  const mergeSelectedOption = (options, selected) => {
    if (!selected?.value && selected?.value !== 0) return options;
    if (options.some((option) => sameId(option.value, selected.value))) {
      return options;
    }
    return [selected, ...options];
  };

  const stripWebsiteProtocol = (value) =>
    String(value || "").trim().replace(/^https?:\/\//i, "");

  const genderOptions = genderOptionList.map((val) => ({
    value: val.gndrId,
    label: val.name,
    id: val.gndrId,
  }));

  const selectedGender =
    genderOptions.find((option) => sameId(option.value, genderValue)) || null;

  const isValidWebsiteUrl = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return true;

    try {
      const normalizedValue = /^https?:\/\//i.test(trimmedValue)
        ? trimmedValue
        : `https://${trimmedValue}`;
      const parsedUrl = new URL(normalizedValue);
      return Boolean(parsedUrl.hostname && parsedUrl.hostname.includes("."));
    } catch {
      return false;
    }
  };

  const validateWebsite = (value) => {
    if (value.trim() !== "" && !isValidWebsiteUrl(value)) {
      setWebsiteError("Please provide a valid website URL.");
    } else {
      setWebsiteError("");
    }
    setWebsiteContent(value);
  };

  const getGenderData = () => {
    axios
      .get(`/Master/GetGenderDDL?LangKey=${language}`)
      .then((response) => {
        const list = response.data.responseData || [];
        setGenderOptionList((prev) => {
          const extras = prev.filter(
            (item) => !list.some((gender) => sameId(gender.gndrId, item.gndrId))
          );
          return extras.length ? [...extras, ...list] : list;
        });
      })
      .catch(console.error);
  };

  const getCountry = () => {
    axios
      .get(
        `Master/GetCountryDDL?search=${countrySearch || ""}&LangKey=${language}`
      )
      .then((response) => {
        const res = response.data.responseData || [];
        const mapped = res.map((item) => ({
          value: item.countyId,
          label: item.countryName,
          code: item.phoneCode,
        }));
        setCountryData(mergeSelectedOption(mapped, selectedCountryRef.current));
      })
      .catch(console.error);
  };

  const getCity = () => {
    axios
      .get(
        `Master/GetCitiesDDL?search=${citySearch || ""}&CountryID=${countryId || ""}&LangKey=${language}`
      )
      .then((response) => {
        const res = response.data.responseData || [];
        const mapped = res.map((item) => ({
          value: item.cityId,
          label: item.cityName,
        }));
        setCityData(mergeSelectedOption(mapped, selectedCityRef.current));
      })
      .catch(console.error);
  };

  const getWebsiteValue = (info = {}) =>
    pickValue(info, ["website", "websiteUrl", "userWebsite", "webSiteLink"]);

  const getAboutValue = (info = {}) =>
    pickValue(info, ["about", "aboutContent", "userAbout"]);

  const mergeProfileSources = (...sources) => ({
    dob: pickFromSources(sources, ["dob"]),
    genderId: pickFromSources(sources, ["genderId", "gndrId", "genderID"]),
    genderName: pickFromSources(sources, ["genderName"]),
    cityID: pickFromSources(sources, ["cityID", "cityId"]),
    cityName: pickFromSources(sources, ["cityName"]),
    countryID: pickFromSources(sources, ["countryID", "countryId", "cntryID"]),
    countryName: pickFromSources(sources, ["countryName"]),
    phoneCode: pickFromSources(sources, ["phoneCode"]),
    website: pickFromSources(sources, ["website", "websiteUrl", "userWebsite", "webSiteLink"]),
    about: pickFromSources(sources, ["about", "aboutContent", "userAbout"]),
    firstName: pickFromSources(sources, ["firstName"]),
    lastName: pickFromSources(sources, ["lastName"]),
    phoneNumber: pickFromSources(sources, ["phoneNumber"]),
  });

  const applyProfileInfo = (info = {}) => {
    const dobValue = pickValue(info, ["dob"]);
    if (dobValue !== undefined) {
      const parsedDob = safeParse(dobValue);
      setStartDate(parsedDob ? parsedDob.toDate() : null);
    }

    const nextGenderId = pickValue(info, ["genderId", "gndrId", "genderID"]);
    const nextGenderName = pickValue(info, ["genderName"]);
    if (nextGenderId !== undefined) {
      setGenderValue(nextGenderId);
      if (nextGenderName) {
        setGenderOptionList((prev) => {
          if (prev.some((item) => sameId(item.gndrId, nextGenderId))) return prev;
          return [{ gndrId: nextGenderId, name: nextGenderName }, ...prev];
        });
      }
    }

    const nextCountryId = pickValue(info, ["countryID", "countryId", "cntryID"]);
    const nextCountryName = pickValue(info, ["countryName"]);
    if (nextCountryId !== undefined) {
      setCountryId(nextCountryId);
      if (nextCountryName) {
        const option = {
          value: nextCountryId,
          label: nextCountryName,
          code: pickValue(info, ["phoneCode"]) || "",
        };
        selectedCountryRef.current = option;
        setSelectedCountry(option);
        setCountryData((prev) => mergeSelectedOption(prev, option));
      }
    }

    const nextCityId = pickValue(info, ["cityID", "cityId"]);
    const nextCityName = pickValue(info, ["cityName"]);
    if (nextCityId !== undefined) {
      setCityId(nextCityId);
      if (nextCityName) {
        const option = {
          value: nextCityId,
          label: nextCityName,
        };
        selectedCityRef.current = option;
        setSelectedCity(option);
        setCityData((prev) => mergeSelectedOption(prev, option));
      }
    }

    const website = getWebsiteValue(info);
    if (website) setWebsiteContent(stripWebsiteProtocol(website));
    const about = getAboutValue(info);
    if (about) setAboutContent(about);

    const nextFirstName = pickValue(info, ["firstName"]);
    if (nextFirstName !== undefined) setFirstName(nextFirstName);
    const nextLastName = pickValue(info, ["lastName"]);
    if (nextLastName !== undefined) setLastName(nextLastName);
    const nextPhoneNumber = pickValue(info, ["phoneNumber"]);
    if (nextPhoneNumber !== undefined) setPhoneNumber(nextPhoneNumber);

    setWebsiteError("");
  };

  const fetchProfileWebsiteAbout = async () => {
    const userUid = localStorage.getItem("userUid");
    if (!userUid || !sessionId) return {};

    try {
      const response = await axios.post("UserProfile/GetUserProfile", {
        deviceType: "Web",
        userUid,
        langKey: language,
      });
      return response.data?.responseData?.profileInfo || {};
    } catch (error) {
      console.error("Error fetching profile website/about:", error);
      return {};
    }
  };

  const prefetchFormData = async () => {
    setIsPrefetching(true);
    try {
      const [ownInfo, profileInfo] = await Promise.all([
        getOwnInfo({ forceRefresh: true }),
        fetchProfileWebsiteAbout(),
      ]);

      applyProfileInfo(
        mergeProfileSources(ownInfo, profileInfo, initialProfileInfo)
      );
    } catch (error) {
      console.error("Error prefetching profile basic info:", error);
    } finally {
      setIsPrefetching(false);
    }
  };

  useEffect(() => {
    selectedCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  useEffect(() => {
    selectedCityRef.current = selectedCity;
  }, [selectedCity]);

  useEffect(() => {
    getGenderData();
    if (initialProfileInfo) {
      applyProfileInfo(initialProfileInfo);
    }
    const prefetchTimer = setTimeout(() => {
      prefetchFormData();
    }, 0);

    return () => clearTimeout(prefetchTimer);
  }, []);

  useEffect(() => {
    if (countrySearch) {
      getCountry();
    }
  }, [countrySearch]);

  useEffect(() => {
    if (citySearch) {
      getCity();
    }
  }, [citySearch, countryId]);

  const preventYearScroll = (datePickerInstance) => {
    const yearElement = datePickerInstance.currentYearElement;
    if (yearElement) {
      yearElement.addEventListener("wheel", (e) => {
        e.preventDefault();
      });
    }
  };

  const appendBaseFields = (formData) => {
    formData.append("DeviceType", "Web");
    formData.append("sessionId", sessionId);
    formData.append("Language", langKey);
  };

  const getApiErrorMessage = (error) => {
    const responseData = error?.response?.data;

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message.trim();
    }

    const validationErrors = responseData?.errors;
    if (validationErrors && typeof validationErrors === "object") {
      const firstValidationMessage = Object.values(validationErrors)
        .flat()
        .find((message) => typeof message === "string" && message.trim());

      if (firstValidationMessage) {
        return firstValidationMessage.trim();
      }
    }

    if (typeof responseData?.title === "string" && responseData.title.trim()) {
      return responseData.title.trim();
    }

    return "Something went wrong.";
  };

  const handleApiError = (error) => {
    if (error?.response?.status === 404) {
      setWarningToastMessage(getApiErrorMessage(error));
      setShowWarningToast(true);
    } else {
      setErrorToastMessage(getApiErrorMessage(error));
      setShowErrorToast(true);
    }
  };

  const handleSave = async () => {
    if (
      !startDate ||
      !countryId ||
      !genderValue ||
      !firstName ||
      !lastName ||
      !cityId
    ) {
      setWarningToastMessage(t("modals.Please fill all the required fields"));
      setShowWarningToast(true);
      return;
    }

    if (websiteContent.trim() !== "" && !isValidWebsiteUrl(websiteContent)) {
      setWarningToastMessage("Please provide a valid website URL.");
      setShowWarningToast(true);
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("DeviceType", "Web");
      formData.append("sessionId", sessionId);
      formData.append("Dob", formatDate(startDate, "YYYY-MM-DD"));
      formData.append("CityID", cityId);
      formData.append("GenderId", genderValue);
      formData.append("LastName", lastName);
      formData.append("PhoneNumber", phoneNumber);
      formData.append("CntryID", countryId);
      formData.append("FirstName", firstName);
      formData.append("Language", langKey);

      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);

      const res = await axios.post("/UserProfile/UpdateCompleteInfo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      let lastMessage = res.data.message;

      if (websiteContent.trim()) {
        const websiteForm = new FormData();
        appendBaseFields(websiteForm);
        websiteForm.append("Website", websiteContent.trim());
        const websiteRes = await axios.post("/UserAccount/SetWebsite", websiteForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastMessage = websiteRes.data.message;
      }

      if ((aboutContent || "").trim()) {
        const aboutForm = new FormData();
        appendBaseFields(aboutForm);
        aboutForm.append("AboutContent", aboutContent.trim());
        const aboutRes = await axios.post("/UserAccount/SetAbout", aboutForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastMessage = aboutRes.data.message;
      }

      setToastMessage(lastMessage);
      setShowToast(true);
      invalidateOwnInfo();
      invalidateUserConfig();
      await getUserProfile();
      onClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Portal layer="modal">
      <div
        className="modal fade show postAdd wlj-overlay-modal"
        tabIndex="-1"
        role="dialog"
        style={{ display: "flex" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div
            className="modal-content d-flex justify-content-around"
            ref={modalContentRef}
            style={{ overflow: "visible" }}
          >
            <div className="modalBorder d-flex justify-content-between align-items-center p-2">
              <h6>{t("User Profile.Profile Basic Info")}</h6>
              <CloseSharpIcon
                className="material-symbols-sharp me-0 pointer"
                onClick={onClose}
              />
            </div>

            <div className="p-3" style={{ minHeight: "420px", maxHeight: "70vh", overflowY: "auto" }}>
                <div className="row">
                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.Date Of Birth")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <div className="dateofbirth w-100">
                      <Flatpickr
                        className="form-control"
                        value={startDate}
                        onChange={(date) => setStartDate(date[0])}
                        placeholder={t("Common.SelectDate")}
                        options={{
                          maxDate: "today",
                          onReady: (_selectedDates, _dateStr, instance) => {
                            preventYearScroll(instance);
                          },
                          onOpen: (_selectedDates, _dateStr, instance) => {
                            preventYearScroll(instance);
                            const scrollEl = modalContentRef.current?.querySelector(".p-3");
                            if (!scrollEl) return;
                            const onScroll = () => instance.close();
                            scrollEl.addEventListener("scroll", onScroll, { passive: true });
                            instance._closeOnScroll = () =>
                              scrollEl.removeEventListener("scroll", onScroll);
                          },
                          onClose: (_selectedDates, _dateStr, instance) => {
                            instance._closeOnScroll?.();
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.Gender")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <ReactSelect
                      className="NewGenderAdd"
                      value={selectedGender}
                      options={genderOptions}
                      onChange={(val) => setGenderValue(val?.value || null)}
                      placeholder={t("Newsfeed.Select Gender")}
                      styles={customStyles} {...wljSelectPortalProps}
                      classNamePrefix="react-select"
                      getOptionValue={(option) => String(option.value)}
                    />
                  </div>

                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.Country")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      value={selectedCountry}
                      isClearable
                      options={countryData}
                      onChange={(selectedOption) => {
                        setSelectedCountry(selectedOption || null);
                        selectedCountryRef.current = selectedOption || null;
                        setCountryId(selectedOption ? selectedOption.value : "");
                        setSelectedCity(null);
                        selectedCityRef.current = null;
                        setCityId(null);
                        setCityData([]);
                        setCitySearch("");
                      }}
                      placeholder={t("SearchJob.Select Country")}
                      onInputChange={(inputValue, { action }) => {
                        if (action === "input-change") {
                          setCountrySearch(inputValue);
                        }
                      }}
                      styles={customStyles} {...wljSelectPortalProps}
                      classNamePrefix="react-select"
                      getOptionValue={(option) => String(option.value)}
                    />
                  </div>

                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.City")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      value={selectedCity}
                      onChange={(selectedOption) => {
                        setSelectedCity(selectedOption || null);
                        selectedCityRef.current = selectedOption || null;
                        setCityId(selectedOption ? selectedOption.value : null);
                      }}
                      options={cityData}
                      noOptionsMessage={() => t("modals.Search and Select City")}
                      placeholder={t("Common.SelectCity")}
                      onInputChange={(inputValue, { action }) => {
                        if (action === "input-change") {
                          setCitySearch(inputValue);
                        }
                      }}
                      styles={customStyles} {...wljSelectPortalProps}
                      classNamePrefix="react-select"
                      getOptionValue={(option) => String(option.value)}
                      isClearable
                    />
                  </div>

                  <div className="col-md-12 mb-3 userInput">
                    <label className="f-14 mb-1">{t("User Profile.Website")}</label>
                    <div className="web-input">
                      <div className="input-group input-group-alt">
                        <div className="input-group-prepend">
                          <span className="input-group-text">http://</span>
                        </div>
                        <input
                          type="text"
                          className="form-control pb-0 px-2"
                          placeholder={t("Common.ExampleCom")}
                          value={websiteContent}
                          onChange={(e) => validateWebsite(e.target.value)}
                        />
                      </div>
                      {websiteError && (
                        <div
                          style={{
                            color: "red",
                            marginTop: "5px",
                            fontSize: "12px",
                          }}
                        >
                          {websiteError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-12 mb-2 userInput about-textArea">
                    <label className="f-14 mb-1">
                      {t("User Profile.About")}
                      {/* <span className="text-danger ms-1">*</span> */}
                    </label>
                    <textarea
                      rows="4"
                      cols="55"
                      value={aboutContent}
                      onChange={(e) => setAboutContent(e.target.value)}
                      maxLength={500}
                    />
                    <p className="info">{aboutContent?.length || 0}/500</p>
                  </div>
                </div>

                {aboutContent?.length >= 500 && (
                  <p className="text-danger f-14 mb-0">
                    {t("User Profile.About content should be less than 500 words")}
                  </p>
                )}

                <div className="col-md-12 text-end mt-3 mb-2 d-flex justify-content-end align-items-center">
                  {isPrefetching && (
                    <Loader className="spinner me-2" size={18} />
                  )}
                  <button
                    type="button"
                    className="mainBtn1 me-2"
                    onClick={handleSave}
                    disabled={isPrefetching || isSaving || !!websiteError}
                    style={{ minWidth: "90px" }}
                  >
                    {isSaving ? t("User Profile.Submit") : t("User Profile.Submit")}
                  </button>
                </div>
              </div>
          </div>
        </div>
      </div>
      </Portal>

      <SuccessToast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
      />
      <WarningToast
        show={showWarningToast}
        message={warningToastMessage}
        onClose={() => setShowWarningToast(false)}
      />
      <ErrorToast
        show={showErrorToast}
        message={errorToastMessage}
        onClose={() => setShowErrorToast(false)}
      />
    </>
  );
};

export default ProfileBasicInfoModal;
